/**
 * Web Audio API helper — проигрывает короткие "приятные" звуки без файлов.
 *
 * Использует OscillatorNode + GainNode с envelope (attack/decay) чтобы
 * звук был не "дрынь" а аккуратный "блинк".
 *
 * Автоплей браузера: первый звук можно проиграть ТОЛЬКО после user gesture.
 * Поэтому `prime()` должен быть вызван из onClick пользователя (например,
 * при включении toggle в Notifications.tsx). После prime() все звуки играют.
 */

let ctx: AudioContext | null = null;
let primed = false;

function getCtx(): AudioContext {
  if (!ctx) {
    const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctor = W.AudioContext ?? W.webkitAudioContext;
    if (!Ctor) throw new Error('Web Audio API не поддерживается');
    ctx = new Ctor();
  }
  return ctx;
}

/** Вызвать из user gesture (клик) — разблокирует автоплей. */
export async function primeAudio(): Promise<void> {
  try {
    const c = getCtx();
    if (c.state === 'suspended') await c.resume();
    // Тихий sample чтобы "разогреть" AudioContext
    const osc = c.createOscillator();
    const g = c.createGain();
    g.gain.value = 0.0001;
    osc.connect(g).connect(c.destination);
    osc.start();
    osc.stop(c.currentTime + 0.05);
    primed = true;
  } catch (e) {
    console.warn('[sound] prime failed:', e);
  }
}

/**
 * Одиночный тон с короткой envelope.
 * @param freq Гц (например 880 = A5)
 * @param durMs Длительность в мс (по умолчанию 140мс)
 * @param type Тип волны: 'sine'|'triangle'|'square' (sine/triangle мягче)
 */
function tone(freq: number, durMs = 140, type: OscillatorType = 'sine', gainPeak = 0.18): void {
  if (!primed) return;
  try {
    const c = getCtx();
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    const t0 = c.currentTime;
    const t1 = t0 + durMs / 1000;
    // ADSR-lite envelope: attack 8ms, sustain, decay 60ms
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gainPeak, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t1);
    osc.connect(g).connect(c.destination);
    osc.start(t0);
    osc.stop(t1 + 0.02);
  } catch (e) {
    console.warn('[sound] tone failed:', e);
  }
}

/** Проиграть пресет звука. */
export type SoundPreset = 'login' | 'joinRoom' | 'leaveRoom' | 'logout' | 'gameWon' | 'gameLost';

const PRESETS: Record<SoundPreset, () => void> = {
  // Восходящая мажорная терция — "кто-то вошёл"
  login: () => {
    tone(660, 120); // E5
    setTimeout(() => tone(880, 140), 80); // A5
  },
  // Двойной мягкий "клик" — "вошёл в комнату"
  joinRoom: () => {
    tone(740, 100);
    setTimeout(() => tone(988, 130), 70);
  },
  // Нисходящий — "вышел"
  leaveRoom: () => {
    tone(660, 110);
    setTimeout(() => tone(523, 130), 80);
  },
  // Одинокий нисходящий — "ушёл с платформы"
  logout: () => {
    tone(440, 200);
  },
  // Победный аккорд — "игра завершена, есть победитель"
  gameWon: () => {
    tone(523, 130); // C5
    setTimeout(() => tone(659, 130), 90); // E5
    setTimeout(() => tone(784, 200), 180); // G5
  },
  // Грустный минорный — "игра завершена, нет победителя" (edge case)
  gameLost: () => {
    tone(440, 200);
    setTimeout(() => tone(330, 250), 120);
  },
};

export function play(preset: SoundPreset): void {
  PRESETS[preset]();
}

/**
 * Показывает браузерный Notification (OS-level) с дефолтным звуком системы.
 * Используется как fallback когда Web Audio не сработал (свёрнутая вкладка).
 * Требует permission Notification.
 */
export async function notifyOs(title: string, body: string): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'default') {
    try { await Notification.requestPermission(); } catch { /* ignore */ }
  }
  if (Notification.permission !== 'granted') return;
  try {
    new Notification(title, {
      body,
      icon: '/icons/icon-192.png',
      tag: 'bura-crm-notification'
    });
  } catch { /* ignore */ }
}